/*
 * Copyright (c) 2021. World Wide Generation Ltd
 */

import React, { useEffect, useState } from 'react';
import { useHistory, useRouteMatch } from 'react-router-dom';
import { Button } from 'reactstrap';
import { generateUrlWithQuery } from '../../routes/util';
import { ROUTES } from '../../constants/routes';
import { activateUser } from '../../actions/user';
import './styles.scss';
import { useSelector } from 'react-redux';
import { getCurrentUser } from '../../selectors/user';
import { Loader } from '@g17eco/atoms/loader';
import { getDefaultConfig } from '../../config/app-config';
import { AppConfig } from '../../types/app';
import G17Client from '../../services/G17Client';
import { ActivationToken } from '../../types/onboarding';
import { TODO_QUERY_PARAM } from '../../constants/todo';
import { getAnalytics } from '../../services/analytics/AnalyticsService';
import { AnalyticsEvents } from '../../services/analytics/AnalyticsEvents';
import { QUESTION, SURVEY } from '@constants/terminology';


interface Props {
  appConfig: AppConfig
}

const CompleteActivation = ({ appConfig }: Props) => {

  const isActive = useSelector(getCurrentUser)?.active;
  const history = useHistory();
  const match = useRouteMatch<{ surveyId: string, initiativeId: string, token: string }>();
  const { surveyId, initiativeId } = match.params;

  const [token, setToken] = useState<{ loaded: boolean, errorMessage: string, data?: ActivationToken }>({
    loaded: false,
    errorMessage: '',
  });
  const tokenId = match.params.token;

  useEffect(() => {
    if (!tokenId) {
      return;
    }
    G17Client.checkActivationToken(tokenId)
      .then((tokenData) => setToken({
        data: tokenData,
        loaded: true,
        errorMessage: ''
      }))
      .catch((e) => setToken({ loaded: false, errorMessage: e.message }));
  }, [tokenId]);

  const redirect = () => {
    const returnPath = generateUrlWithQuery(ROUTES.COMPANY_TRACKER_SURVEY, {
      rootAppPath: appConfig.rootAppPath ?? getDefaultConfig().branding.ctlApp.rootAppPath,
      initiativeId: initiativeId,
      surveyId: surveyId,
      page: 'overview',
    }, { [TODO_QUERY_PARAM]: '1' });

    if (isActive) {
      // Logged in and active
      history.push(returnPath);
      return;
    }

    const tokenData = token.data;
    if (tokenData) {
      return activateUser({}, tokenData.userId, tokenData.token, returnPath);
    }
  }

  useEffect(() => {
    getAnalytics().track(AnalyticsEvents.OnboardingCompleted);
  }, []);

  if (tokenId && !token.loaded && !token.errorMessage) {
    return <section className='title-container'><Loader relative size={'sm'} /></section>;
  }

  if ((!isActive && !token) || token.errorMessage) {
    return <>
      <section className='title-container'>
        <div className='h1'>Invalid Activation</div>
        <p>Please click on your activation e-mail again or contact our support.</p>
      </section>
    </>;
  }

  return <>
    <section className='title-container'>
      <div className='h1'>Done!</div>
      <div className='h1 mb-3'>Your {SURVEY.SINGULAR} {QUESTION.PLURAL} are now ready</div>
      <p>Answering these {QUESTION.PLURAL} means you can show impact <br />towards your ESG goals, as well as <br /> the UN Sustainable Development Goals (SDGs)</p>
    </section>
    <div className='mt-5 text-right mr-4'>
      <Button color='primary' onClick={redirect}>Continue to your {SURVEY.SINGULAR}</Button>
    </div>
  </>;
}

export default CompleteActivation;
